<template>
  <section class="cl">
    <header class="cl__head">
      <div>
        <h2 class="cl__title">{{ title }}</h2>
        <div v-if="subtitle" class="cl__sub">{{ subtitle }}</div>
      </div>
      <slot name="actions"></slot>
    </header>

    <div v-if="!rows.length" class="cl__empty">{{ emptyText }}</div>

    <div v-else class="cl__table">
      <div class="cl__row cl__row--head">
        <div>Broj</div>
        <div>Oštećeni</div>
        <div>Faza</div>
        <div>Obaveza</div>
        <div class="cl__right">{{ metricLabel }}</div>
      </div>
      <div
        v-for="row in rows"
        :key="row.id"
        class="cl__row"
        :style="{ borderLeftColor: edgeColor(row) }"
        @click="$router.push('/predmet/view/' + row.id)"
      >
        <div class="cl__broj">{{ row.broj }}</div>
        <div class="cl__truncate" :title="row.osteceni">{{ row.osteceni || "—" }}</div>
        <div><StatusTag :value="row.status" /></div>
        <div class="cl__truncate cl__muted" :title="(row.obaveza || []).join(', ')">
          {{ (row.obaveza || []).join(", ") || "—" }}
        </div>
        <div class="cl__right" :class="metricClass(row)">{{ metricText(row) }}</div>
      </div>
    </div>

    <div v-if="footer" class="cl__foot">{{ footer }}</div>
  </section>
</template>

<script>
import StatusTag from "@/components/StatusTag";
import { STALE_DAYS, WARN_DAYS } from "@/models/status";

export default {
  name: "CaseList",
  components: { StatusTag },
  props: {
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    rows: { type: Array, default: () => [] },
    footer: { type: String, default: "" },
    emptyText: { type: String, default: "Nema predmeta" },
    // "dani" shows days without movement, "saldo" shows an amount
    metric: { type: String, default: "dani" },
    metricLabel: { type: String, default: "Bez pomaka" },
  },
  methods: {
    days(row) {
      return row.dana_bez_pomaka !== undefined ? row.dana_bez_pomaka : row.dana;
    },
    metricText(row) {
      if (this.metric === "saldo") {
        const parts = Number(row.saldo || 0).toFixed(2).split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        return parts.join(",");
      }
      const d = this.days(row);
      if (d === null || d === undefined) return "—";
      return d === 1 ? "1 dan" : d + " dana";
    },
    metricClass(row) {
      const d = this.days(row);
      if (d === null || d === undefined) return "";
      if (d >= STALE_DAYS) return "cl__metric--stale";
      if (d >= WARN_DAYS) return "cl__metric--warn";
      return "";
    },
    edgeColor(row) {
      const d = this.days(row);
      if (d >= STALE_DAYS) return "#9b3345";
      if (d >= WARN_DAYS) return "#c9a227";
      return "transparent";
    },
  },
};
</script>

<style lang="scss" scoped>
.cl {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.cl__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px 16px;
}

.cl__title {
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--ink);
  margin: 0;
}

.cl__sub {
  font-size: var(--fs-sm);
  color: var(--ink-4);
  margin-top: 2px;
}

.cl__table {
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
}

.cl__row {
  display: grid;
  grid-template-columns: 96px minmax(0, 1.3fr) 140px minmax(0, 1fr) 104px;
  gap: 12px;
  align-items: center;
  padding: 9px 16px;
  border-bottom: 1px solid var(--line-soft);
  border-left: 3px solid transparent;
  font-size: var(--fs-md);
  cursor: pointer;

  &:hover {
    background: var(--surface-alt);
  }
}

.cl__row--head {
  position: sticky;
  top: 0;
  background: var(--surface-alt);
  border-top: 1px solid var(--line);
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--ink-3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: default;

  &:hover {
    background: var(--surface-alt);
  }
}

.cl__broj {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cl__muted {
  color: var(--ink-2);
}

.cl__truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cl__right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.cl__metric--warn {
  color: var(--warn);
  font-weight: 500;
}

.cl__metric--stale {
  color: var(--danger);
  font-weight: 600;
}

.cl__empty {
  padding: 32px;
  text-align: center;
  font-size: var(--fs-md);
  color: var(--ink-4);
}

.cl__foot {
  padding: 9px 16px;
  border-top: 1px solid var(--line);
  font-size: var(--fs-sm);
  color: var(--ink-3);
}
</style>
